import { useCallback, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import MyVendorCard from "./MyVendorCard";
import MyVendorsListGrid from "./MyVendorsListGrid";
import { splitMyVendorsForDisplay } from "../../utils/myVendorsConstants";

function fillerPlaceholders(count) {
  const remainder = count % 3;
  return remainder === 0 ? 0 : 3 - remainder;
}

function VendorGroup({
  label,
  vendors,
  keyPrefix,
  sponsorId,
  onVendorAction,
  onRequestAction,
  selectedVendorIds,
  onToggleVendorSelection,
  isOpen,
  onToggleOpen,
  isBookmarked,
  isVendorActionPending,
}) {
  if (!vendors.length) return null;
  const placeholders = fillerPlaceholders(vendors.length);

  return (
    <section className="mt-8 first:mt-0">
      <button
        type="button"
        onClick={() => onToggleOpen(!isOpen)}
        className="group flex w-full items-center gap-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
      >
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 group-hover:text-gray-600 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
        <span>
          {label} ({vendors.length})
        </span>
      </button>
      {isOpen ? (
        <div className="mt-4 grid grid-cols-1 items-start gap-6 sm:grid-cols-2 md:grid-cols-3">
          {vendors.map((vendor) => (
            <MyVendorCard
              key={vendor.id}
              vendor={vendor}
              sponsorId={sponsorId}
              onVendorAction={onVendorAction}
              onRequestAction={onRequestAction}
              isSelected={selectedVendorIds.has(String(vendor.id))}
              onToggleSelection={(event) => {
                event.stopPropagation();
                onToggleVendorSelection?.(vendor);
              }}
              isBookmarked={isBookmarked?.(vendor.id)}
              isBookmarkPending={isVendorActionPending?.(vendor.id, "bookmark")}
              isHidePending={isVendorActionPending?.(vendor.id, "hide")}
              isUnhidePending={isVendorActionPending?.(vendor.id, "unhide")}
            />
          ))}
          {Array.from({ length: placeholders }, (_, index) => (
            <div key={`${keyPrefix}-placeholder-${index}`} className="invisible" aria-hidden="true" />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Vendor layout grouped by platform vs manual membership source (card view only). */
export default function MyVendorsVendorSections({
  vendors = [],
  viewMode = "card",
  sponsorId = null,
  onVendorAction,
  onRequestAction,
  selectedVendorIds = new Set(),
  onToggleVendorSelection,
  allFilteredSelected = false,
  someFilteredSelected = false,
  onSelectAllFiltered,
  isBookmarked,
  isVendorActionPending,
}) {
  const [platformOpen, setPlatformOpen] = useState(true);
  const [manualOpen, setManualOpen] = useState(true);
  const [hiddenOpen, setHiddenOpen] = useState(false);

  const handleHiddenAdded = useCallback(() => {
    setHiddenOpen(true);
  }, []);

  const wrappedVendorAction = useCallback(
    async (actionId, vendor) => {
      if (actionId === "add-to-hidden") {
        handleHiddenAdded();
      }
      await onVendorAction?.(actionId, vendor);
    },
    [handleHiddenAdded, onVendorAction]
  );

  if (!vendors.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-gray-900">No vendors match the current search or filters.</p>
        <p className="mt-2 text-sm text-gray-500">Try adjusting filters or clearing your search.</p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <MyVendorsListGrid
        vendors={vendors}
        sponsorId={sponsorId}
        selectedVendorIds={selectedVendorIds}
        onToggleVendorSelection={onToggleVendorSelection}
        allFilteredSelected={allFilteredSelected}
        someFilteredSelected={someFilteredSelected}
        onSelectAllFiltered={onSelectAllFiltered}
        onVendorAction={onVendorAction}
        onRequestAction={onRequestAction}
        isBookmarked={isBookmarked}
        isVendorActionPending={isVendorActionPending}
      />
    );
  }

  const { requestedInPlatform, addedManually, hidden } = splitMyVendorsForDisplay(vendors);

  return (
    <div>
      <VendorGroup
        label="Requested in Platform"
        vendors={requestedInPlatform}
        keyPrefix="platform"
        sponsorId={sponsorId}
        onVendorAction={wrappedVendorAction}
        onRequestAction={onRequestAction}
        selectedVendorIds={selectedVendorIds}
        onToggleVendorSelection={onToggleVendorSelection}
        isOpen={platformOpen}
        onToggleOpen={setPlatformOpen}
        isBookmarked={isBookmarked}
        isVendorActionPending={isVendorActionPending}
      />
      <VendorGroup
        label="Added Manually"
        vendors={addedManually}
        keyPrefix="manual"
        sponsorId={sponsorId}
        onVendorAction={wrappedVendorAction}
        onRequestAction={onRequestAction}
        selectedVendorIds={selectedVendorIds}
        onToggleVendorSelection={onToggleVendorSelection}
        isOpen={manualOpen}
        onToggleOpen={setManualOpen}
        isBookmarked={isBookmarked}
        isVendorActionPending={isVendorActionPending}
      />
      <VendorGroup
        label="Hidden"
        vendors={hidden}
        keyPrefix="hidden"
        sponsorId={sponsorId}
        onVendorAction={wrappedVendorAction}
        onRequestAction={onRequestAction}
        selectedVendorIds={selectedVendorIds}
        onToggleVendorSelection={onToggleVendorSelection}
        isOpen={hiddenOpen}
        onToggleOpen={setHiddenOpen}
        isBookmarked={isBookmarked}
        isVendorActionPending={isVendorActionPending}
      />
    </div>
  );
}
